import React from 'react';
import { tw } from '../index';
import { Data } from '../interfaces';
import { useTranslation } from 'react-i18next';
import Link from '../components/Link';
import { getBrandData } from '../services/brands';

interface DataProps {
  dt: Data | null;
  fontTitleColor: string;
  fontTitle: string;
  fontBodyColor: string;
  divider: string;
  module: string;
  brand: string;
  country: string;
}

function Heading({
  id,
  text,
  fontTitleColor,
  fontTitle,
}: {
  id: string;
  text: string;
  fontTitleColor: string;
  fontTitle: string;
}) {
  return (
    <h3 className={tw`text-xl mb-4 ${fontTitleColor} ${fontTitle}`} id={id}>
      {text}
    </h3>
  );
}

function InfoRow({
  label,
  value,
  labelClasses,
  valueClasses,
}: {
  label: string;
  value: string | number;
  labelClasses?: string;
  valueClasses?: string;
}) {
  return (
    <div role='text'>
      <div className={tw`${labelClasses || 'text-sm'}`}>{label}</div>
      <div className={tw`${valueClasses || 'text-xl'}`}>{value}</div>
    </div>
  );
}

function ManufacturingSection({
  dt,
  fontTitleColor,
  fontTitle,
  fontBodyColor,
  t,
  divider,
}: {
  dt: Data;
  fontTitleColor: string;
  fontTitle: string;
  fontBodyColor: string;
  t: Function;
  divider: string;
}) {
  return (
    <>
      <section aria-labelledby='manufacturing-conditions'>
        <Heading
          id='manufacturing-conditions'
          text={t('Manufacturing_conditions')}
          fontTitleColor={fontTitleColor}
          fontTitle={fontTitle}
        />
        <div className={tw`grid grid-cols-2 gap-4 pt-0`}>
          <InfoRow
            label={t('Waste_recovery')}
            value={`${dt.manufacturing.plantWasteRecyclingRate}%`}
            valueClasses={tw`text-xl ${fontTitleColor} ${fontTitle}`}
          />
          <InfoRow
            label={t('Renewable_energy')}
            value={`${dt.manufacturing.plantRenewableEnergyRate}%`}
            valueClasses={tw`text-xl ${fontTitleColor} ${fontTitle}`}
          />
        </div>
        <div className={tw`mt-6`} role='text'>
          <div className={tw`text-sm`}>
            {dt.manufacturing.madeInSubcontractorPlant
              ? t('Made_in_a_factory_committed_to_responsible_production')
              : t('Made_in_a_responsible_plant')}
          </div>
          <div className={tw`text-xl ${fontTitleColor} ${fontTitle}`}>{t('Yes')}</div>
        </div>
      </section>
      <div className={tw`${divider} w-full my-6`} role='separator' />
    </>
  );
}

function PackagingSection({
  dt,
  fontTitleColor,
  fontTitle,
  fontBodyColor,
  t,
  divider,
}: {
  dt: Data;
  fontTitleColor: string;
  fontTitle: string;
  fontBodyColor: string;
  t: Function;
  divider: string;
}) {
  return (
    <>
      <section aria-labelledby='environmental-impact-packaging'>
        <Heading
          id='environmental-impact-packaging'
          text={t('Environmental_impact_of_the_packaging')}
          fontTitleColor={fontTitleColor}
          fontTitle={fontTitle}
        />
        <div className={tw`grid grid-cols-2 items-center grid-rows-2 gap-4`}>
          <div >
            <InfoRow
              label={t('Recyclable')}
              value={dt.packaging.mainComponentFamilyRecyclability ? t('Yes') : t('No')}
              valueClasses={tw`text-xl ${fontTitleColor} ${fontTitle}`}
            />
          </div>
          <div role='text'>
            <div className={tw`text-xl ${fontTitleColor} ${fontTitle}`}>
              {dt.packaging.recycledMaterialRate}%
            </div>

            <div className={tw`text-sm`}>{t('of_bottle_made_of_recycled_material')}</div>
          </div>
          <div role='text'>
            <InfoRow
              label={t('Refillable_or_reusable')}
              value={dt.packaging.rechargeableRefillable ? t('Yes') : t('No')}
              valueClasses={tw`text-xl ${fontTitleColor} ${fontTitle}`}
            />
          </div>
          {Number(dt.packaging.forestCertificationRate) > 0 && (
            <div role='text'>
              <div className={tw`text-xl ${fontTitleColor} ${fontTitle}`}>
                {dt.packaging.forestCertificationRate}%
              </div>
              <div className={tw`text-sm`}>{t('cardbord')}</div>
            </div>
          )}
        </div>
      </section>
      {/* <div className={tw`${divider} w-full my-6`} role='separator' /> */}
    </>
  );
}

function SocialImpactSection({
  dt,
  fontTitleColor,
  fontTitle,
  fontBodyColor,
  t,
}: {
  dt: Data;
  fontTitleColor: string;
  fontTitle: string;
  fontBodyColor: string;
  t: Function;
}) {
  return (
    <section aria-labelledby='social-impact-title'>
      <Heading
        id='social-impact-title'
        text={t('Social_impact_title')}
        fontTitleColor={fontTitleColor}
        fontTitle={fontTitle}
      />
      <p className={tw`text-base leading-7 mb-4 ${fontBodyColor}`}>{t('Social_impact_Intro')}</p>
      <div className={tw`text-base leading-7 mb-4`} role='text'>
        <span className={tw`text-xl ${fontTitleColor} ${fontTitle}`}>
          {dt.social.nbOfInclusiveSuppliers}
        </span>
        <span>
          {dt.social.nbOfInclusiveSuppliers > 1
            ? t('Social_impact_Info_Plural')
            : t('Social_impact_Info_Singular')}
        </span>
      </div>
    </section>
  );
}

function FootnotesSection({
  dt,
  fontTitleColor,
  fontTitle,
  t,
  brand,
  country,
}: {
  dt: Data;
  fontTitleColor: string;
  fontTitle: string;
  t: Function;
  brand: string;
  country: string;
}) {
  const brandData = getBrandData(brand, country);
  return (
    <section aria-labelledby='footnotes'>
      <h3 className={tw`sr-only`} id='footnotes'>
        {t('Footnotes')}
      </h3>
      <div className={tw`text-xs space-y-1 mt-4`}>
        <p>{t('Footnotes-1')}</p>
        <p>{t('Footnotes-2')}</p>
        {false && dt.manufacturing.madeInSubcontractorPlant && <p>{t('Footnotes-3')}</p>}
      </div>
      {brandData && brandData['Link to CSR landing page'] && (
        <div className={tw`text-base inline-block mt-4`}>
          <Link
            href={brandData['Link to CSR landing page']}
            datalayerValue='learn-csr'
            text={t('Footnotes-link-1')}
          />
          <span> {t('Footnotes-link-2')}</span>
        </div>
      )}
    </section>
  );
}

function Csr({ dt, fontTitleColor, fontTitle, fontBodyColor, divider, module, brand, country }: DataProps) {
  const { t } = useTranslation();
  if (!dt) return null;

  return (
    <div className={tw`mt-0`}>
      <div className={tw`${fontBodyColor}`}>
        {/* Header */}
        <div className={tw`flex flex-col space-y-1.5 pt-3 pb-4 ${fontTitleColor} ${fontTitle}`}>
          <h2 className={tw`text-xl font-semibold`} id='additional-sustainability-facts'>
            {module === 'fullMode'
              ? t('ADDITIONAL_SUSTAINABILITY_FACTS')
              : t('SUSTAINABILITY_FACTS')}
          </h2>
        </div>
        <div className={tw`pb-6 pt-0 space-y-6`}>
          <ManufacturingSection
            dt={dt}
            fontTitleColor={fontTitleColor}
            fontTitle={fontTitle}
            fontBodyColor={fontBodyColor}
            t={t}
            divider={divider}
          />
          <PackagingSection
            dt={dt}
            fontTitleColor={fontTitleColor}
            fontTitle={fontTitle}
            fontBodyColor={fontBodyColor}
            t={t}
            divider={divider}
          />
          {/* <SocialImpactSection
            dt={dt}
            fontTitleColor={fontTitleColor}
            fontTitle={fontTitle}
            fontBodyColor={fontBodyColor}
            t={t}
          />
           */}
          <FootnotesSection dt={dt} fontTitleColor={fontTitleColor} fontTitle={fontTitle} t={t} brand={brand} country={country} />
        </div>
      </div>
    </div>
  );
}

export default Csr;
